import React from "react";
import NavTop from "../../component/NavTop";
import Modal from "../../component/Modal";
import NumberFormat from "react-number-format";
import {
  IoIosAdd,
  IoIosClose,
  IoIosTrash,
  IoIosCheckmark,
  IoIosCheckmarkCircle,
  IoIosTime,
  IoIosPaperPlane,
  IoIosAlert,
  IoIosEye,
} from "react-icons/io";
import { FiArrowUpRight, FiArrowDownLeft } from "react-icons/fi";
import { FormatNumber as FormatN } from "../../helpers";
import { toast } from "react-toastify";
import { FormatNumber, FormatDate } from "../../helpers";
import {
  useDebt,
  useWalletAmount,
  debtAddPayment,
  debtDelete,
  debtClose,
  debtDeletePayment,
  debtSendConfirmation,
  debtAcknowledgeDispute,
  debtEditAndResend,
  KIND_UTANG,
} from "../../api";
import "./debt.css";

const DebtDetail = (props) => {
  const rawId = props.match.params.id;
  // Routes render unwrapped in App.js so /debts/add and /debts/parties/...
  // also match /debts/:id. Skip fetch (and render) for reserved sub-paths
  // to avoid 404 noise.
  const RESERVED = ["add", "parties"];
  const id = RESERVED.includes(rawId) ? null : rawId;
  const { debt, isLoading, refresh } = useDebt(id);
  const { walletAmounts } = useWalletAmount();

  const [payModal, setPayModal] = React.useState(false);
  const [confirmDel, setConfirmDel] = React.useState(false);
  const [payAmount, setPayAmount] = React.useState(0);
  const [payDate, setPayDate] = React.useState(
    new Date().toISOString().slice(0, 10)
  );
  const [payWallet, setPayWallet] = React.useState("");
  const [payNotes, setPayNotes] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [sendModal, setSendModal] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [editAmtModal, setEditAmtModal] = React.useState(false);
  const [ackModal, setAckModal] = React.useState(false);
  const [editAmount, setEditAmount] = React.useState(0);
  const [editDesc, setEditDesc] = React.useState("");
  const [ackNote, setAckNote] = React.useState("");
  const [resolving, setResolving] = React.useState(false);

  React.useEffect(() => {
    if (debt && payAmount === 0) setPayAmount(debt.outstanding);
  }, [debt, payAmount]);

  React.useEffect(() => {
    if (!payWallet && walletAmounts.length > 0) {
      setPayWallet(walletAmounts[0].id);
    }
  }, [walletAmounts, payWallet]);

  if (!id) return null;

  if (isLoading) {
    return (
      <>
        <NavTop title="Detail" />
        <div className="main-padding text-center text-muted">Memuat…</div>
      </>
    );
  }
  if (!debt) {
    return (
      <>
        <NavTop title="Detail" />
        <div className="main-padding text-center text-danger">
          Tidak ditemukan
        </div>
      </>
    );
  }

  const isUtang = debt.kind === KIND_UTANG;
  const kindLabel = isUtang ? "Utang" : "Piutang";
  const pct = Math.min(
    100,
    Math.round((debt.paidAmount / debt.originalAmount) * 100) || 0
  );

  const submitPayment = async () => {
    if (payAmount <= 0) return toast.error("Masukkan jumlah");
    if (payAmount > debt.outstanding + 0.001)
      return toast.error("Melebihi sisa utang");
    if (!payWallet) return toast.error("Pilih wallet");
    setSubmitting(true);
    try {
      await debtAddPayment(id, {
        amount: payAmount,
        walletId: payWallet,
        paidAt: new Date(payDate + "T00:00:00").toISOString(),
        notes: payNotes.trim() || undefined,
      });
      toast.success("Pembayaran tercatat");
      setPayModal(false);
      setPayNotes("");
      setPayAmount(0);
      refresh();
    } catch (err) {}
    setSubmitting(false);
  };

  const onDelete = async () => {
    try {
      await debtDelete(id);
      toast.success("Dihapus");
      props.history.push("/debts");
    } catch (err) {}
  };

  const onClose = async () => {
    if (!window.confirm("Tutup utang ini (write-off)? Tidak bisa di-undo selain hapus.")) return;
    try {
      await debtClose(id);
      toast.success("Ditutup");
      refresh();
    } catch (err) {}
  };

  const onDeletePayment = async (pid) => {
    if (!window.confirm("Hapus pembayaran ini?")) return;
    try {
      await debtDeletePayment(id, pid);
      toast.success("Dihapus");
      refresh();
    } catch (err) {}
  };

  const sendConfirmation = async () => {
    setSending(true);
    try {
      await debtSendConfirmation(id);
      toast.success("Konfirmasi terkirim via WhatsApp");
      setSendModal(false);
      refresh();
    } catch (err) {}
    setSending(false);
  };

  const openEditAmount = () => {
    setEditAmount(debt.originalAmount);
    setEditDesc(debt.description || "");
    setEditAmtModal(true);
  };

  const submitEditAndResend = async () => {
    if (editAmount <= 0) return toast.error("Masukkan jumlah");
    if (editAmount < debt.paidAmount)
      return toast.error("Jumlah baru tidak boleh kurang dari yang sudah dibayar");
    setResolving(true);
    try {
      await debtEditAndResend(id, {
        originalAmount: editAmount,
        description: editDesc.trim() || undefined,
      });
      toast.success("Tersimpan & konfirmasi baru terkirim");
      setEditAmtModal(false);
      refresh();
    } catch (err) {}
    setResolving(false);
  };

  const submitAcknowledge = async () => {
    setResolving(true);
    try {
      await debtAcknowledgeDispute(id, {
        resolutionNote: ackNote.trim() || undefined,
      });
      toast.success("Keberatan diselesaikan");
      setAckModal(false);
      setAckNote("");
      refresh();
    } catch (err) {}
    setResolving(false);
  };

  const buildPreview = () => {
    if (!debt) return "";
    const amountStr = "Rp " + FormatN(Math.round(debt.originalAmount));
    const kindPhrase = isUtang
      ? "pinjaman saya kepada Anda"
      : "pinjaman Anda kepada saya";
    const dueLine = debt.dueDate
      ? "📅 Jatuh tempo " + FormatDate(debt.dueDate) + "\n"
      : "";
    const descLine = debt.description
      ? "📝 " + debt.description + "\n"
      : "";
    return (
      "Halo " + debt.party.name + " 👋\n\n" +
      "Saya, [nama Anda], mencatat " + kindPhrase + ":\n\n" +
      "💰 " + amountStr + "\n" +
      dueLine + descLine + "\n" +
      "Bisa Anda konfirmasi? Tap link berikut:\n" +
      "[link konfirmasi unik]\n\n" +
      "Link berlaku 30 hari.\n— via Cashflow"
    );
  };

  return (
    <>
      <NavTop title="Detail" />
      <div className="main-padding debt-detail">
        <div className="debt-detail__hero">
          <div
            className={`debt-detail__kind debt-detail__kind--${
              isUtang ? "utang" : "piutang"
            }`}
          >
            {isUtang ? <FiArrowUpRight /> : <FiArrowDownLeft />} {kindLabel}
          </div>
          <div
          className="debt-detail__party"
          style={{ cursor: "pointer", textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: "3px" }}
          onClick={() =>
            props.history.push(`/debts/parties/${debt.party.id}`)
          }
        >
          {debt.party.name}
        </div>
          <div className="debt-detail__outstanding-label">Sisa</div>
          <div className="debt-detail__outstanding">
            Rp {FormatNumber(Math.round(debt.outstanding))}
          </div>
          <div
            className={`debt-detail__progress ${
              debt.status === "paid" ? "debt-detail__progress--paid" : ""
            } ${debt.status === "overdue" ? "debt-detail__progress--overdue" : ""}`}
          >
            <div
              className="debt-detail__progress-bar"
              style={{ width: pct + "%" }}
            />
          </div>
          <div className="debt-detail__progress-label">
            <span>
              Lunas Rp {FormatNumber(Math.round(debt.paidAmount))} ({pct}%)
            </span>
            <span>Pokok Rp {FormatNumber(Math.round(debt.originalAmount))}</span>
          </div>
          <div className="debt-detail__status-row">
            {debt.dueDate && (
              <small
                className={`debt-card__meta-due ${
                  debt.daysOverdue ? "debt-card__meta-due--overdue" : ""
                }`}
              >
                <IoIosTime /> Jatuh tempo {FormatDate(debt.dueDate)}
                {debt.daysOverdue
                  ? ` (telat ${debt.daysOverdue} hari)`
                  : ""}
              </small>
            )}
          </div>
        </div>

        {debt.status !== "paid" && debt.status !== "written_off" && (
          <button
            className="btn btn-block btn-primary debt-detail__action"
            onClick={() => setPayModal(true)}
          >
            <IoIosAdd /> Catat Pembayaran
          </button>
        )}

        {debt.confirmation && debt.confirmation.state === "disputed" ? (
          <DisputeResolutionPanel
            confirmation={debt.confirmation}
            partyName={debt.party.name}
            onEdit={openEditAmount}
            onAcknowledge={() => setAckModal(true)}
            onDelete={() => setConfirmDel(true)}
          />
        ) : (
          <ConfirmationCard
            confirmation={debt.confirmation || { state: "none" }}
            partyName={debt.party.name}
            partyPhone={debt.party.phone}
            onSend={() => setSendModal(true)}
          />
        )}

        <div className="debt-detail__timeline-title">Riwayat</div>
        <div className="debt-detail__timeline">
          <div className="debt-detail__tl-item">
            <span className="debt-detail__tl-icon debt-detail__tl-icon--origin">
              {isUtang ? <FiArrowUpRight /> : <FiArrowDownLeft />}
            </span>
            <div className="debt-detail__tl-body">
              <div className="debt-detail__tl-label">Pokok</div>
              <div className="debt-detail__tl-date">
                {FormatDate(debt.startedAt)}
              </div>
            </div>
            <span
              className={`debt-detail__tl-amount debt-detail__tl-amount--${
                isUtang ? "in" : "out"
              }`}
            >
              {isUtang ? "+" : "-"}Rp {FormatNumber(Math.round(debt.originalAmount))}
            </span>
          </div>

          {debt.payments.map((p) => (
            <div className="debt-detail__tl-item" key={p.id}>
              <span
                className={`debt-detail__tl-icon debt-detail__tl-icon--${
                  isUtang ? "out" : "in"
                }`}
              >
                <IoIosCheckmark />
              </span>
              <div className="debt-detail__tl-body">
                <div className="debt-detail__tl-label">
                  {isUtang ? "Bayar" : "Terima"}
                  {p.notes ? ` — ${p.notes}` : ""}
                </div>
                <div className="debt-detail__tl-date">
                  {FormatDate(p.paidAt)}
                </div>
              </div>
              <span
                className={`debt-detail__tl-amount debt-detail__tl-amount--${
                  isUtang ? "out" : "in"
                }`}
              >
                {isUtang ? "-" : "+"}Rp {FormatNumber(Math.round(p.amount))}
              </span>
              <button
                className="debt-detail__tl-del"
                onClick={() => onDeletePayment(p.id)}
                title="Hapus pembayaran"
              >
                <IoIosClose />
              </button>
            </div>
          ))}
        </div>

        <div className="d-flex justify-content-between mt-4">
          {debt.status !== "written_off" && debt.status !== "paid" && (
            <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
              Tutup manual
            </button>
          )}
          <button
            className="btn btn-sm btn-outline-secondary ml-auto"
            onClick={() => setConfirmDel(true)}
          >
            <IoIosTrash /> Hapus
          </button>
        </div>
      </div>

      <Modal
        show={payModal}
        title="Catat Pembayaran"
        onClose={() => setPayModal(false)}
        onPositive={submitPayment}
        onNegative={() => setPayModal(false)}
        textPositive={submitting ? "Menyimpan…" : "Simpan"}
        textNegative="Batal"
      >
        <div className="form-group">
          <label>Jumlah</label>
          <NumberFormat
            prefix={"Rp "}
            value={payAmount}
            className="form-control"
            thousandSeparator={true}
            onValueChange={(v) => setPayAmount(v.floatValue || 0)}
          />
          <small className="text-muted">
            Sisa: Rp {FormatNumber(Math.round(debt.outstanding))}
          </small>
        </div>
        <div className="form-group">
          <label>Wallet</label>
          <select
            className="form-control"
            value={payWallet}
            onChange={(e) => setPayWallet(e.target.value)}
          >
            {walletAmounts.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Tanggal</label>
          <input
            type="date"
            className="form-control"
            value={payDate}
            onChange={(e) => setPayDate(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Catatan (opsional)</label>
          <input
            type="text"
            className="form-control"
            value={payNotes}
            onChange={(e) => setPayNotes(e.target.value)}
            placeholder="mis. cicilan ke-3"
          />
        </div>
      </Modal>

      <Modal
        show={confirmDel}
        title="Hapus debt?"
        onClose={() => setConfirmDel(false)}
        onPositive={onDelete}
        onNegative={() => setConfirmDel(false)}
        textPositive="Hapus"
        textNegative="Batal"
      >
        Transaksi cashflow yang terkait juga akan dihapus. Tidak bisa di-undo.
      </Modal>

      <Modal
        show={editAmtModal}
        title="Edit jumlah & minta konfirmasi ulang"
        onClose={() => setEditAmtModal(false)}
        onPositive={submitEditAndResend}
        onNegative={() => setEditAmtModal(false)}
        textPositive={resolving ? "Memproses…" : "Simpan & Kirim WA"}
        textNegative="Batal"
      >
        <p style={{ fontSize: "0.85rem", marginBottom: "0.5rem" }}>
          Setelah simpan, konfirmasi baru otomatis dikirim ke{" "}
          <strong>{debt && debt.party && debt.party.name}</strong>.
        </p>
        <div className="form-group">
          <label>
            <small className="text-muted">Jumlah baru</small>
          </label>
          <NumberFormat
            prefix={"Rp "}
            value={editAmount}
            className="form-control"
            thousandSeparator={true}
            onValueChange={(v) => setEditAmount(v.floatValue || 0)}
          />
          {debt && debt.paidAmount > 0 && (
            <small className="text-muted">
              Sudah dibayar Rp {FormatN(Math.round(debt.paidAmount))} — jumlah
              baru tidak boleh di bawah ini.
            </small>
          )}
        </div>
        <div className="form-group mb-0">
          <label>
            <small className="text-muted">Keterangan (opsional)</small>
          </label>
          <input
            type="text"
            className="form-control"
            value={editDesc}
            onChange={(e) => setEditDesc(e.target.value)}
            placeholder="contoh: disesuaikan sesuai keberatan"
          />
        </div>
      </Modal>

      <Modal
        show={ackModal}
        title="Anggap selesai, pertahankan catatan"
        onClose={() => setAckModal(false)}
        onPositive={submitAcknowledge}
        onNegative={() => setAckModal(false)}
        textPositive={resolving ? "Memproses…" : "Selesaikan"}
        textNegative="Batal"
      >
        <p style={{ fontSize: "0.85rem" }}>
          Catatan tetap{" "}
          <strong>Rp {debt && FormatN(Math.round(debt.originalAmount))}</strong>.
          Keberatan {debt && debt.party && debt.party.name} tetap tercatat di
          riwayat, tapi tidak ada permintaan konfirmasi ulang.
        </p>
        <div className="form-group mb-0">
          <label>
            <small className="text-muted">Catatan resolusi (opsional)</small>
          </label>
          <textarea
            className="form-control"
            rows="2"
            value={ackNote}
            onChange={(e) => setAckNote(e.target.value.slice(0, 500))}
            placeholder='contoh: "Sudah ngobrol langsung, sepakat lanjut"'
          />
          <small className="text-muted">{ackNote.length}/500</small>
        </div>
      </Modal>

      <Modal
        show={sendModal}
        title="Kirim konfirmasi via WhatsApp"
        onClose={() => setSendModal(false)}
        onPositive={sendConfirmation}
        onNegative={() => setSendModal(false)}
        textPositive={sending ? "Mengirim…" : "Kirim Sekarang"}
        textNegative="Batal"
      >
        <p style={{ fontSize: "0.85rem", marginBottom: "0.5rem" }}>
          Pesan berikut akan dikirim ke{" "}
          <strong>{debt && debt.party && debt.party.phone}</strong>:
        </p>
        <div className="debt-confirm-preview">{buildPreview()}</div>
        <small className="text-muted">
          Link konfirmasi akan unik untuk debt ini, berlaku 30 hari.
        </small>
      </Modal>
    </>
  );
};

const stateMeta = {
  none: { label: "Belum dikirim", iconCls: "" },
  sent: { label: "Terkirim, belum dilihat", iconCls: "" },
  viewed: { label: "Sudah dilihat", iconCls: "" },
  confirmed: { label: "Dikonfirmasi", iconCls: "debt-confirm__icon--confirmed" },
  disputed: { label: "Dibantah", iconCls: "debt-confirm__icon--disputed" },
  expired: { label: "Link kedaluwarsa", iconCls: "debt-confirm__icon--expired" },
  acknowledged: {
    label: "Keberatan diselesaikan",
    iconCls: "debt-confirm__icon--confirmed",
  },
};

const DisputeResolutionPanel = ({
  confirmation,
  partyName,
  onEdit,
  onAcknowledge,
  onDelete,
}) => {
  return (
    <div className="dispute-panel">
      <div className="dispute-panel__header">
        <span className="dispute-panel__icon">
          <IoIosAlert />
        </span>
        <div>
          <div className="dispute-panel__title">Dibantah oleh {partyName}</div>
          <div className="dispute-panel__sub">
            {FormatDate(confirmation.disputedAt)}
          </div>
        </div>
      </div>
      {confirmation.disputeReason && (
        <div className="dispute-panel__quote">
          "{confirmation.disputeReason}"
        </div>
      )}
      <div className="dispute-panel__steps-label">Langkah selanjutnya</div>
      <div className="dispute-panel__actions">
        <button
          type="button"
          className="dispute-action dispute-action--primary"
          onClick={onEdit}
        >
          <span className="dispute-action__icon">
            <IoIosPaperPlane />
          </span>
          <span className="dispute-action__body">
            <span className="dispute-action__label">
              Edit jumlah & minta konfirmasi ulang
            </span>
            <small className="dispute-action__hint">
              Sesuaikan amount sesuai keberatan, WA baru otomatis terkirim
            </small>
          </span>
        </button>
        <button
          type="button"
          className="dispute-action dispute-action--default"
          onClick={onAcknowledge}
        >
          <span className="dispute-action__icon">
            <IoIosCheckmark />
          </span>
          <span className="dispute-action__body">
            <span className="dispute-action__label">
              Anggap selesai, pertahankan catatan
            </span>
            <small className="dispute-action__hint">
              Catatan tetap, keberatan tercatat di riwayat
            </small>
          </span>
        </button>
        <button
          type="button"
          className="dispute-action dispute-action--danger"
          onClick={onDelete}
        >
          <span className="dispute-action__icon">
            <IoIosTrash />
          </span>
          <span className="dispute-action__body">
            <span className="dispute-action__label">Hapus debt ini</span>
            <small className="dispute-action__hint">
              Hapus selamanya + transaksi cashflow terkait
            </small>
          </span>
        </button>
      </div>
    </div>
  );
};

const ConfirmationCard = ({ confirmation, partyName, partyPhone, onSend }) => {
  const s = confirmation.state || "none";
  const meta = stateMeta[s] || stateMeta.none;
  const Icon = ({ s }) => {
    if (s === "confirmed") return <IoIosCheckmarkCircle />;
    if (s === "disputed") return <IoIosAlert />;
    if (s === "viewed") return <IoIosEye />;
    if (s === "sent") return <IoIosPaperPlane />;
    if (s === "expired") return <IoIosTime />;
    return <IoIosPaperPlane />;
  };
  const subtext = (() => {
    if (s === "sent" || s === "viewed")
      return `Dikirim ${FormatDate(confirmation.sentAt)}`;
    if (s === "confirmed")
      return `${partyName} mengkonfirmasi ${FormatDate(confirmation.confirmedAt)}`;
    if (s === "disputed")
      return `${partyName} membantah ${FormatDate(confirmation.disputedAt)}`;
    if (s === "acknowledged")
      return `Diselesaikan ${FormatDate(confirmation.resolvedAt)}`;
    if (s === "expired") return "Link sudah expire, kirim ulang.";
    if (!partyPhone) return "Tambah nomor WA counterparty dulu.";
    return "Minta konfirmasi dari counterparty";
  })();
  const canSend = !!partyPhone && s !== "confirmed" && s !== "acknowledged";

  return (
    <div className="debt-confirm">
      <span className={`debt-confirm__icon ${meta.iconCls}`}>
        <Icon s={s} />
      </span>
      <div className="debt-confirm__body">
        <div className="debt-confirm__label">Konfirmasi counterparty</div>
        <div className="debt-confirm__value">{meta.label}</div>
        <div className="debt-confirm__sub">{subtext}</div>
        {s === "disputed" && confirmation.disputeReason && (
          <span className="debt-confirm__dispute">
            "{confirmation.disputeReason}"
          </span>
        )}
        {s === "acknowledged" && confirmation.resolutionNote && (
          <small className="text-muted d-block mt-1" style={{ fontStyle: "italic" }}>
            "{confirmation.resolutionNote}"
          </small>
        )}
      </div>
      {canSend && (
        <button
          className={`debt-confirm__action ${
            s === "none" ? "" : "debt-confirm__action--ghost"
          }`}
          onClick={onSend}
        >
          <IoIosPaperPlane />{" "}
          {s === "none" ? "Kirim WA" : "Kirim ulang"}
        </button>
      )}
    </div>
  );
};

export default DebtDetail;
